import { z } from 'zod';
import { Text, Loader, Button, Modal, Stack, Group, Box } from '@mantine/core';
import { useSearchParams } from 'react-router-dom';
import { useDebouncedValue } from '@mantine/hooks';
import { useContext, useState, useCallback, useEffect } from 'react';
import { ComboboxStringItem, ComboboxLikeRenderOptionInput } from '@mantine/core';
import { logger, ErrorType } from '../../utils/logger';

import Form from 'components/form';
import SEO from 'components/SEO';
import { getNames } from 'utils/string';
import useReferer from 'hooks/useReferer';
import AuthContext from 'context/AuthContext';
import useFormHandler from 'hooks/useFormHandler';
import { schemaFields } from 'utils/schemaBuilder';
import BusinessContext from 'context/BusinessContext';
import TermsAndPolicy from 'components/TermsAndPolicy';
import { normalizeUrl, extractDomain } from 'utils/url';
import TrackingContext from 'context/TrackingContext';
import Testimonial from 'components/Testimonial';
import { useWebsiteValidation } from 'hooks/useWebsiteValidation';

interface BusinessOption {
  id: string;
  displayName: {
    text: string;
  };
  formattedAddress: string;
}

type CustomOption = {
  value: string;
  label: string;
  description?: string;
};

const Onboarding = () => {
  const [searchParams] = useSearchParams();
  const referer = useReferer();
  const [search, setSearch] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [websiteFromPlaces, setWebsiteFromPlaces] = useState<string>('');
  const [businessOptions, setBusinessOptions] = useState<Array<BusinessOption>>([]);
  const [showWebsiteWarning, setShowWebsiteWarning] = useState<boolean>(false);
  const [showOnboardingProgress, setShowOnboardingProgress] = useState<boolean>(false);
  const [hasWebsite, setHasWebsite] = useState<boolean>(false);

  const {
    actions: { getBusinesses, getPlaceDetails },
  } = useContext(BusinessContext);
  const {
    actions: { signupWithWebsite },
    data: { registerEmail, registerPassword },
  } = useContext(AuthContext);
  const {
    actions: { trackFailedSignUp },
  } = useContext(TrackingContext);
  const [debouncedSearch] = useDebouncedValue(search, 200);

  // Use the new website validation hook
  const {
    isValidating: websiteLoading,
    validationResult,
    error: websiteError,
    isSpa,
    validateUrl,
  } = useWebsiteValidation(1500);

  const formSchema = z.object({
    business_name: z.string().min(1, 'Please enter or select a company name'),
    website: z.string().optional().or(z.literal('')),
    phone: schemaFields.phoneNumber.schema,
    full_name: schemaFields.fullName.schema,
    location: z.object({
      city: z.string(),
      region: z.string(),
      country: z.string(),
      latitude: z.number(),
      longitude: z.number(),
    }),
  });

  const {
    form,
    error,
    setError,
    loading: formLoading,
  } = useFormHandler(formSchema, {
    initialValues: {
      website: '',
      full_name: '',
      business_name: '',
      phone: '',
      location: {
        city: '',
        region: '',
        country: '',
        latitude: 0,
        longitude: 0,
      },
    },
  });

  useEffect(() => {
    const searchBusinesses = async () => {
      if (debouncedSearch.length < 2) {
        setBusinessOptions([]);
        setLoading(false);
        return;
      }
      setLoading(true);

      try {
        const results = await getBusinesses(debouncedSearch);
        setBusinessOptions(results);
      } catch (error) {
        logger.error('Failed to search businesses', error as Error, {
          errorType: ErrorType.API_ERROR,
          component: 'onboarding'
        });
        setBusinessOptions([]);
      }
      setLoading(false);
    };

    searchBusinesses();
  }, [debouncedSearch, getBusinesses]);

  // Set form errors based on website validation
  useEffect(() => {
    if (websiteError) {
      form.setFieldError('website', websiteError);
    } else if (form.errors.website) {
      // Only clear if there was previously an error to avoid unnecessary re-renders
      form.clearFieldError('website');
    }
  }, [websiteError, form]);

  /**
   * Handles the onboarding form submission with complex business validation rules.
   * 
   * Business Rules:
   * 1. Website validation is recommended but not required
   * 2. If no website provided, show confirmation modal
   * 3. SPAs (Single Page Applications) are allowed despite validation warnings
   * 4. Only block submission for actual reachability errors on non-SPA sites
   * 
   * @param values - Form values including business name, website, phone, location
   * @throws Will show error modal for validation failures or API errors
   */
  const onSubmit = async (values: z.infer<typeof formSchema>) => {
    // Check if user is trying to submit without a website - show warning modal
    if (!values.website?.trim()) {
      setShowWebsiteWarning(true);
        return;
      }

    // Only block submission for actual errors, not SPAs
    if (values.website && websiteError && !isSpa) {
      form.setFieldError('website', 'Please enter a valid and reachable website');
        return;
      }

    await proceedWithSignup(values, true);
  };

  /**
   * Completes the business registration process with full system initialization.
   * 
   * 🤖 AI Context: This function orchestrates the final step of user onboarding, integrating
   * multiple business systems (auth, analytics, billing, referrals) into a cohesive flow.
   * Key business decisions and external dependencies are documented for AI understanding.
   * 
   * Business Flow:
   * 1. User data normalization (name parsing, referral attribution)
   * 2. Account creation via backend API with business context
   * 3. Automatic authentication and session establishment
   * 4. Analytics event tracking for marketing attribution
   * 5. Third-party integrations (Rewardful affiliates, GA4)
   * 
   * External Dependencies:
   * - Rewardful: Affiliate tracking system for partner commissions
   * - Google Analytics: Marketing attribution and conversion tracking
   * - Backend Auth API: Account creation and JWT token generation
   * - Navigation system: Post-signup redirect handling
   * 
   * Error Handling Strategy:
   * - Form validation errors: Display inline with field context
   * - API failures: Show user-friendly messages, track for analytics
   * - Network issues: Graceful degradation with retry suggestions
   * 
   * @param values - Validated form data from onboarding schema
   * @param hasWebsiteProvided - Whether user included website for AI training
   */
  const proceedWithSignup = async (values: z.infer<typeof formSchema>, hasWebsiteProvided: boolean = false) => {
    setHasWebsite(hasWebsiteProvided);
    setShowOnboardingProgress(true);
    
    const { first_name, last_name } = getNames(values.full_name);
    const { full_name, ...rest } = values;
    const refer_id =
      (window as any).Rewardful?.referral ||
      searchParams.get('via') ||
      undefined;
    const coupon = (window as any).Rewardful?.coupon?.id || undefined;
    const email = registerEmail || '';

    try {
      await signupWithWebsite({
        ...rest,
        referer,
        refer_id,
        coupon,
        last_name,
        first_name,
        email,
        password: registerPassword || '',
      });
      // Success - modal will be closed by navigation/redirect
    } catch (error: any) {
      setShowOnboardingProgress(false);
      if (error?.response?.status === 400 && error.response.data?.detail) {
        setError(error.response.data.detail);
        trackFailedSignUp(error.response.data.detail);
        return;
      }
      trackFailedSignUp(`${error}`);
      setError(
        'An error occurred while creating your company. Please try again.'
      );
    }
  };

  const handleBusinessSearch = useCallback(async (query: string) => {
    setSearch(query);
    // Clear business name error when user starts typing
    if (form.errors.business_name) {
      form.clearFieldError('business_name');
    }
  }, [form]);

  const handleWebsiteChange = (value: string) => {
    const normalizedUrl = normalizeUrl(value);
    form.setFieldValue('website', normalizedUrl);
    validateUrl(value);
    setWebsiteFromPlaces('');
    // Always clear website error when user starts typing
    form.clearFieldError('website');
  };

  const handleWebsiteBlur = () => {
    const currentValue = form.values.website;
    if (currentValue?.trim()) {
      // Just trigger validation - let the hook handle format checking
      validateUrl(currentValue);
    }
  };

  const handleBusinessSelect = async (value: string) => {
    if (value === search) {
      form.setValues({
        business_name: value,
      });
      return;
    }

    const place = await getPlaceDetails(value);

    if (place) {
      const formValues: Partial<z.infer<typeof formSchema>> = {
        business_name: place.displayName?.text,
        location: place.location,
      };

      if (!form.values.website && place.websiteUri) {
        const normalizedUrl = normalizeUrl(place.websiteUri);
        handleWebsiteChange(normalizedUrl);
      } else if (place.websiteUri) {
        const normalizedUrl = normalizeUrl(place.websiteUri);
        setWebsiteFromPlaces(normalizedUrl);
      }
      
      form.setValues(formValues);
    }
  };

  const getOptions = () => {
    const options = businessOptions.map((option: BusinessOption) => ({
      value: option.id,
      label: option.displayName.text,
      description: option.formattedAddress,
    }));

    if (search.length > 1) {
      const defaultOption = {
        value: search,
        label: search,
        description: 'Select to use this name',
      };

      return [defaultOption, ...options];
    }

    return options;
  };

  const renderOptionForBusinesses = (input: ComboboxLikeRenderOptionInput<ComboboxStringItem>) => {
    const option = input.option as CustomOption;
    
    return (
      <div>
        <Text size="sm" fw={option.value === option.label ? 500 : undefined}>
          {option.value === option.label ? `Use "${option.label}"` : option.label}
        </Text>
        <Text size="xs" c="dimmed">
          {option.description}
        </Text>
      </div>
    );
  };

  const handleUseExtractedBusinessName = (extractedName: string) => {
    // Only update business name, preserve existing location data
    form.setFieldValue('business_name', extractedName);
  };

  // Add handlers to clear errors when user starts typing
  const handleFullNameChange = (value: string | number) => {
    if (form.errors.full_name) {
      form.clearFieldError('full_name');
    }
  };

  const handlePhoneChange = (value: string) => {
    if (form.errors.phone) {
      form.clearFieldError('phone');
    }
  };

  return (
    <>
      <SEO 
        title="Setup Your AI Receptionist - Business Onboarding"
        description="Complete your free AI receptionist setup by adding your business details. Train your virtual receptionist to handle customer calls professionally."
        keywords="AI receptionist setup, business onboarding, virtual assistant training, AI phone system configuration"
      />
    <div className="flex-1 min-h-screen overflow-hidden">
      <div className="min-h-screen overflow-y-auto">
        <nav className="h-[70px] sticky top-0 w-full px-4 md:px-6 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <a href="https://nucleus.com/" tabIndex={8}>
              <img src="/images/nucleus-logo.svg" alt="Nucleus Logo" className="h-8" />
            </a>
          </div>

          <span></span>
        </nav>
        <div className="md:flex items-center lg:items-center signup-container extend px-4 md:px-0">
          <div className="mx-auto">
            <div className="text-center mb-3 max-w-[600px] mx-auto">
              <h1 className="text-xl md:text-2xl font-semibold text-[#5E6AB2] mb-1">
                Setup Your AI Receptionist - Business Onboarding
              </h1>
              <h2 className="text-base md:text-lg font-semibold text-gray-700 mb-4 text-center">
                Let's train your new AI receptionist!
              </h2>
            </div>

            <div className="border custom-border-color bg-white rounded-xl p-4 md:p-6 md:w-5/6 xl:w-[440px] mx-auto">
            <Form form={form} error={error} onSubmit={onSubmit}>
              <Form.Autocomplete
                loading={loading}
                name="business_name"
                label="Business name"
                data={getOptions()}
                onChange={handleBusinessSearch}
                onOptionSubmit={handleBusinessSelect}
                renderOption={renderOptionForBusinesses}
                placeholder="Search or type your company name"
                tabIndex={1}
                error={
                  search.length > 0 && !form.values.business_name
                    ? 'Please enter or select a company name'
                    : undefined
                }
              />
                <div 
                  className="relative validation-container" 
                  style={{ 
                    minHeight: '96px',
                    maxHeight: (isSpa && validationResult && !websiteError && !websiteLoading) 
                             ? '320px' : 
                             (validationResult?.valid && !isSpa && validationResult.detected_company_name && !websiteLoading) 
                             ? '250px' : '96px'
                  }}
                >
                  <Form.Input
                    type="text"
                    name="website"
                  label="Website URL (Recommended)"
                    placeholder="https://example.com"
                    tabIndex={2}
                    onChange={(value) => handleWebsiteChange(value as string)}
                    onBlur={handleWebsiteBlur}
                    error={websiteError || undefined}
                    style={{ position: 'relative', zIndex: 2 }}
                    rightSection={
                      websiteLoading ? (
                        <div style={{ position: 'relative', zIndex: 3 }}>
                          <Loader size="14" color="#adb5bd" />
                        </div>
                      ) : validationResult?.valid && !isSpa && validationResult?.detected_company_name && !websiteLoading && !websiteError ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '4px', position: 'relative', zIndex: 3 }}>
                          <svg className="w-4 h-4" style={{ color: 'var(--green-color)' }} fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        </div>
                      ) : null
                    }
                    className={
                      websiteError ? '' : (
                        isSpa && validationResult && !websiteLoading ? 'website-warning-border' : 
                        validationResult?.valid && !isSpa && !websiteLoading ? 'website-success-border' : ''
                      )
                    }
                  />
                  
                  {/* SPA Detection Warning - positioned behind input with negative z-index */}
                  {isSpa && validationResult && !websiteError && !websiteLoading && (
                    <div style={{ transform: 'translateY(-36px)', marginTop: '0.25rem', marginBottom: '-1.5rem', position: 'relative', zIndex: 1 }} className="validation-message-enter">
                      <div className="rounded-md" style={{ backgroundColor: 'var(--yellow-light-bg)', border: '1px solid var(--yellow-color)', padding: '20px 12px 12px 12px' }}>
                        <div className="flex items-start">
                          <div className="flex-shrink-0">
                            <svg className="w-5 h-5 mt-0.5" style={{ color: 'var(--yellow-color)' }} fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                            </svg>
                          </div>
                          <div className="ml-3 flex-1">
                            <h4 className="text-sm font-medium" style={{ color: 'var(--text-color)' }}>
                              Dynamic Website Detected
                            </h4>
                            <div className="mt-1 text-sm" style={{ color: 'var(--text-color)' }}>
                              <p>This website loads content dynamically, which may limit how much information we can extract for your AI receptionist.</p>
                              {validationResult.spa_info.frameworks.length > 0 && (
                                <p className="mt-1">
                                  <span className="font-medium">Built with:</span> {validationResult.spa_info.frameworks.join(', ')}
                                </p>
                              )}
                              <p className="mt-1 text-sm">
                                Don't worry - you can continue setting up your account. We'll extract what we can and you can add more details later.
                              </p>
                              {validationResult.detected_company_name && (
                                <div className="mt-2 flex items-center justify-between">
                                  <Text size="xs" style={{ color: 'var(--text-color)' }}>
                                    Found business name <span style={{ fontWeight: 600 }}>"{validationResult.detected_company_name}"</span> from website
                                  </Text>
                                  {validationResult.detected_company_name.toLowerCase() !== form.values.business_name.toLowerCase() && (
                                    <Button
                                      size="sm"
                                      variant="subtle"
                                      onClick={() => handleUseExtractedBusinessName(validationResult.detected_company_name)}
                                      style={{ 
                                        color: 'var(--yellow-color)',
                                        marginLeft: '8px',
                                        whiteSpace: 'nowrap',
                                        minWidth: 'fit-content'
                                      }}
                                    >
                                      Use this name
                                    </Button>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {/* Valid Website Success Message - positioned behind input with negative z-index */}
                  {validationResult?.valid && !isSpa && validationResult.detected_company_name && !websiteLoading && (
                    <div style={{ transform: 'translateY(-36px)', marginTop: '0.25rem', marginBottom: '-1.5rem', position: 'relative', zIndex: 1 }} className="validation-message-enter">
                      <div className="flex items-center justify-between rounded-md" style={{ backgroundColor: '#f0f9f0', border: '1px solid var(--green-color)', padding: '20px 12px 12px 12px' }}>
                        <div className="flex items-center">
                          <Text size="xs" style={{ color: 'var(--text-color)' }}>
                            Found business name <span style={{ fontWeight: 600 }}>"{validationResult.detected_company_name}"</span> from website
                          </Text>
                        </div>
                        {validationResult.detected_company_name.toLowerCase() !== form.values.business_name.toLowerCase() && (
                          <Button
                            size="sm"
                            variant="subtle"
                            onClick={() => handleUseExtractedBusinessName(validationResult.detected_company_name)}
                            style={{ 
                              color: 'var(--green-color)',
                              marginLeft: '8px',
                              whiteSpace: 'nowrap',
                              minWidth: 'fit-content'
                            }}
                          >
                            Use this name
                          </Button>
                        )}
                      </div>
                    </div>
                  )}
                </div>
                
                {websiteFromPlaces &&
                  websiteFromPlaces !== form.values.website && (
                    <div
                      style={{
                        margin: '-1rem 0 1.25rem',
                        minHeight: '44px',
                        display: 'flex',
                        alignItems: 'center'
                      }}
                    >
                      <div
                        style={{
                          color: 'var(--mantine-color-dimmed)',
                          fontSize: 'calc(0.75rem * var(--mantine-scale))',
                          padding: '8px 4px',
                          minHeight: '32px',
                          display: 'flex',
                          alignItems: 'center',
                          cursor: 'pointer'
                        }}
                        onClick={() => handleWebsiteChange(websiteFromPlaces)}
                      >
                        or use {extractDomain(websiteFromPlaces)}?
                      </div>
                    </div>
                  )}
                
              <Form.Input
                type="text"
                name="full_name"
                label="Your full name"
                placeholder="Ex. Michael Douglas"
                tabIndex={3}
                  onChange={handleFullNameChange}
              />
              <Form.PhoneInput
                name="phone"
                label="Your mobile number"
                placeholder="Enter your mobile number"
                tabIndex={4}
                  onChange={handlePhoneChange}
              />
              <Form.Submit
                loading={formLoading}
                tabIndex={5}
                  disabled={
                    formLoading || 
                    websiteLoading || 
                    (!!form.values.website?.trim() && (!validationResult?.valid && !isSpa))
                  }
              >
                Continue
              </Form.Submit>
            </Form>
            </div>
            
            {/* Testimonial - hidden on mobile */}
            <div className="hidden md:block mt-8 max-w-[600px] mx-auto">
              <Testimonial />
            </div>
          </div>
        </div>
        <div className="w-full md:h-[50px]">
          <TermsAndPolicy termsTabIndex={6} privacyTabIndex={7} />
        </div>
      </div>
      
      {/* Website Warning Modal */}
      <Modal
        centered
        radius="md"
        shadow="lg"
        opened={showWebsiteWarning}
        onClose={() => setShowWebsiteWarning(false)}
        withCloseButton={false}
        closeOnClickOutside={false}
        closeOnEscape={false}
      >
        <Stack align="center" mb="xl">
          <Stack gap={4} align="center">
            <Text fw={500} size="lg">
              Add a Website?
            </Text>
            <Text size="sm" c="dimmed" ta="center">
              Adding your website helps us get your AI receptionist set up faster with information about your business. This makes it more helpful from day one.
            </Text>
          </Stack>
        </Stack>

        <Group justify="center" gap="sm">
          <Button 
            size="sm" 
            variant="default" 
            onClick={() => setShowWebsiteWarning(false)}
          >
            Go back & add website
          </Button>
          <Button 
            size="sm" 
            onClick={async () => {
              setShowWebsiteWarning(false);
              await proceedWithSignup(form.values, false);
            }}
          >
            Continue without
          </Button>
        </Group>
      </Modal>

      {/* Onboarding Progress Modal */}
      <Modal
        centered
        opened={showOnboardingProgress}
        onClose={() => {}}
        withCloseButton={false}
        closeOnClickOutside={false}
        closeOnEscape={false}
        radius="lg"
        shadow="lg"
        padding="xl"
        size="sm"
      >
        <Stack align="center" gap="xl" py="md">
          <Box pos="relative">
            <Loader size={40} color="blue" />
          </Box>
          <Stack gap={4} align="center">
            <Text size="lg" fw={500}>
              {hasWebsite ? 'Setting up your AI receptionist' : 'Creating your account'}
            </Text>
            <Text size="sm" c="dimmed" ta="center">
              {hasWebsite 
                ? "We're creating your account and training your AI with your business information. This will only take a moment..."
                : "We're creating your account and getting your AI business number ready. This will only take a moment..."
              }
            </Text>
          </Stack>
        </Stack>
      </Modal>
    </div>
    </>
  );
};

export default Onboarding;
